import { useState, useEffect, useRef } from 'react'
import api from '../../services/api'
import { Award, Download, Calendar, CheckCircle, ExternalLink, Lock, Star, Share2, Linkedin, Copy, X, Sparkles, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { masterclassService } from '../../services/masterclassService'
import LoadingSpinner from '../../components/LoadingSpinner'

// Phase 4E: First-availability congratulations detection using localStorage
const CONGRATS_KEY = 'sma_cert_congrats_seen'

function getSeenCongratsIds() {
  try {
    const raw = localStorage.getItem(CONGRATS_KEY)
    return raw ? new Set(JSON.parse(raw)) : new Set()
  } catch {
    return new Set()
  }
}

function markCongratsSeen(certIds) {
  try {
    const seen = getSeenCongratsIds()
    certIds.forEach(id => seen.add(id))
    localStorage.setItem(CONGRATS_KEY, JSON.stringify([...seen]))
  } catch {
    // localStorage may be unavailable — silently skip
  }
}

// Phase 4E: Certificate preview silhouette (not a real certificate)
function CertificatePreview() {
  return (
    <div className="cinema-glass border border-cinema-gold/20 p-6 sm:p-8 max-w-md mx-auto" aria-label="Certificate preview (not issued)">
      {/* PREVIEW label */}
      <div className="flex items-center justify-center mb-4">
        <span className="px-3 py-1 text-xs font-sans font-semibold uppercase tracking-wider bg-cinema-gold/10 text-cinema-gold border border-cinema-gold/30 rounded-sm">
          Preview
        </span>
      </div>

      {/* Certificate silhouette */}
      <div className="border-2 border-cinema-gold/30 p-5 sm:p-6 bg-cinema-graphite/30 relative">
        {/* Gold corner accents */}
        <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-cinema-gold/40" />
        <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-cinema-gold/40" />
        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-cinema-gold/40" />
        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-cinema-gold/40" />

        <div className="text-center space-y-4 py-4">
          {/* SMA logo area */}
          <div className="flex items-center justify-center gap-2">
            <div className="w-8 h-8 rounded-full border-2 border-cinema-gold/40 flex items-center justify-center">
              <span className="font-display text-xs text-cinema-gold/60 font-semibold">SMA</span>
            </div>
            <span className="font-display text-xs text-cinema-gold/60 uppercase tracking-wider">Synergy Multimedia Academy</span>
          </div>

          {/* Certificate title area */}
          <div className="space-y-1">
            <div className="h-3 w-32 mx-auto bg-cinema-gold/15 rounded-sm" />
            <div className="h-2 w-24 mx-auto bg-white/5 rounded-sm" />
          </div>

          {/* Student name placeholder */}
          <div className="space-y-1 pt-2">
            <div className="h-2 w-16 mx-auto bg-white/5 rounded-sm" />
            <div className="h-4 w-40 mx-auto bg-cinema-soft/10 rounded-sm" />
          </div>

          {/* Course title placeholder */}
          <div className="space-y-1 pt-2">
            <div className="h-2 w-20 mx-auto bg-white/5 rounded-sm" />
            <div className="h-3 w-48 mx-auto bg-cinema-soft/10 rounded-sm" />
          </div>

          {/* Seal placeholder */}
          <div className="flex justify-center pt-3">
            <div className="w-12 h-12 rounded-full border-2 border-cinema-gold/30 flex items-center justify-center">
              <Award size={20} className="text-cinema-gold/30" />
            </div>
          </div>

          {/* Date placeholder */}
          <div className="h-2 w-24 mx-auto bg-white/5 rounded-sm" />
        </div>
      </div>

      <p className="font-sans text-xs text-cinema-muted text-center mt-4 leading-relaxed">
        Complete all lessons and meet the required completion conditions to unlock your certificate.
      </p>
    </div>
  )
}

// Phase 4E: Congratulations panel for newly available certificates
function CongratulationsPanel({ certificates, onDismiss, onView, onDownload }) {
  const panelRef = useRef(null)

  // Restrained entrance animation respecting prefers-reduced-motion
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) {
      setVisible(true)
    } else {
      const timer = setTimeout(() => setVisible(true), 50)
      return () => clearTimeout(timer)
    }
  }, [])

  const firstCert = certificates[0]
  const multiple = certificates.length > 1

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Certificate congratulations"
      className={`cinema-glass border border-cinema-gold/30 bg-cinema-gold/[0.03] p-6 sm:p-8 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        aria-label="Dismiss congratulations message"
        className="absolute top-3 right-3 p-1.5 text-cinema-muted hover:text-white transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center"
      >
        <X size={18} />
      </button>

      <div className="text-center max-w-lg mx-auto">
        {/* Award icon */}
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-cinema-gold/10 border border-cinema-gold/30 flex items-center justify-center">
            <Award size={28} className="text-cinema-gold" />
          </div>
        </div>

        {/* Congratulations message */}
        <h2 className="font-display text-xl sm:text-2xl text-cinema-gold font-medium mb-2">
          Congratulations!
        </h2>
        <p className="font-sans text-sm sm:text-base text-white font-medium mb-1">
          {multiple
            ? `You've completed ${certificates.length} courses.`
            : `You've completed ${firstCert?.course_title || 'your course'}.`}
        </p>
        <p className="font-sans text-xs sm:text-sm text-cinema-muted mb-6">
          {multiple
            ? 'Your certificates are now available.'
            : 'Your certificate is now available.'}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => onView(firstCert?.id, firstCert?.can_download)}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 cinema-glass border border-cinema-gold/30 text-cinema-gold hover:bg-cinema-gold/10 text-sm font-sans font-medium transition-all min-h-[44px]"
          >
            <ExternalLink size={16} /> View Certificate
          </button>
          <button
            onClick={() => onDownload(firstCert?.id, firstCert?.certificate_number, firstCert?.can_download)}
            disabled={!firstCert?.can_download}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 cinema-btn-primary text-sm font-sans font-medium transition-all min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download size={16} /> Download Certificate
          </button>
        </div>

        {/* Continue journey link */}
        <Link
          to="/student/dashboard"
          className="inline-flex items-center gap-1.5 mt-5 font-sans text-xs text-cinema-muted hover:text-cinema-glow transition-colors font-medium min-h-[36px]"
        >
          <Sparkles size={13} /> Continue your creative journey <ArrowRight size={13} />
        </Link>
      </div>
    </div>
  )
}

// Phase 4E: Share Achievement section
function ShareAchievement({ certificate }) {
  const [copied, setCopied] = useState(false)

  if (!certificate || !certificate.can_download) return null

  const certNumber = certificate.certificate_number
  const courseTitle = certificate.course_title || 'my course'
  const verifyUrl = `${window.location.origin}/verify-certificate?number=${encodeURIComponent(certNumber)}`

  const handleLinkedIn = () => {
    const shareText = `I'm proud to have completed "${courseTitle}" at Synergy Multimedia Academy!`
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyUrl)}&summary=${encodeURIComponent(shareText)}`
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer')
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(verifyUrl)
      setCopied(true)
      toast.success('Verification link copied')
      setTimeout(() => setCopied(false), 2500)
    } catch {
      // Fallback for browsers without clipboard API
      const textarea = document.createElement('textarea')
      textarea.value = verifyUrl
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        setCopied(true)
        toast.success('Verification link copied')
        setTimeout(() => setCopied(false), 2500)
      } catch {
        toast.error('Could not copy link')
      }
      document.body.removeChild(textarea)
    }
  }

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My SMA Certificate',
          text: `I completed "${courseTitle}" at Synergy Multimedia Academy!`,
          url: verifyUrl,
        })
      } catch {
        // User cancelled — no action needed
      }
    } else {
      handleCopyLink()
    }
  }

  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share

  return (
    <div className="mt-4 pt-4 border-t border-white/5">
      <p className="font-sans text-xs text-cinema-muted uppercase tracking-wider font-semibold mb-3">Share Achievement</p>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleLinkedIn}
          aria-label="Share certificate on LinkedIn"
          className="inline-flex items-center gap-1.5 px-3 py-2 cinema-glass border border-white/10 text-cinema-soft hover:text-white hover:border-white/20 text-xs font-sans font-medium transition-all min-h-[44px]"
        >
          <Linkedin size={14} /> LinkedIn
        </button>
        <button
          onClick={handleCopyLink}
          aria-label="Copy verification link"
          className="inline-flex items-center gap-1.5 px-3 py-2 cinema-glass border border-white/10 text-cinema-soft hover:text-white hover:border-white/20 text-xs font-sans font-medium transition-all min-h-[44px]"
        >
          <Copy size={14} /> {copied ? 'Copied!' : 'Copy Link'}
        </button>
        {hasNativeShare && (
          <button
            onClick={handleNativeShare}
            aria-label="Share certificate via device sharing"
            className="inline-flex items-center gap-1.5 px-3 py-2 cinema-glass border border-white/10 text-cinema-soft hover:text-white hover:border-white/20 text-xs font-sans font-medium transition-all min-h-[44px]"
          >
            <Share2 size={14} /> Share
          </button>
        )}
      </div>
      <p className="font-sans text-xs text-cinema-muted/70 mt-2.5 leading-relaxed">
        Verification link: {verifyUrl}
      </p>
    </div>
  )
}

export default function Certificates() {
  const [certificates, setCertificates] = useState([])
  const [masterclassCerts, setMasterclassCerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCongrats, setShowCongrats] = useState(false)
  const [congratsCerts, setCongratsCerts] = useState([])
  // Ref to prevent StrictMode double-invoke from marking certs as seen before first render
  const congratsProcessedRef = useRef(false)

  useEffect(() => {
    fetchAll()
  }, [])

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [certsRes, mcRes] = await Promise.allSettled([
        api.get('/certificates/my-certificates'),
        masterclassService.getMyCertificates(),
      ])
      const certsData = certsRes.status === 'fulfilled' ? certsRes.value.data : []
      const mcData = mcRes.status === 'fulfilled' ? (mcRes.value.certificates || []) : []

      // Phase 4E: First-availability congratulations detection
      // Use ref to prevent StrictMode double-invoke from overwriting congrats state
      let newCongratsCerts = null // null = don't update, [] = no new certs, [...] = new certs
      if (certsData.length > 0 && !congratsProcessedRef.current) {
        congratsProcessedRef.current = true
        const seenIds = getSeenCongratsIds()
        newCongratsCerts = certsData.filter(c => !seenIds.has(c.id))
      }

      // Batch all state updates together (React 18 automatic batching)
      setCertificates(certsData)
      setMasterclassCerts(mcData)
      setError(null)
      // Only update congrats state if we actually processed the check (not a StrictMode duplicate)
      if (newCongratsCerts !== null) {
        setCongratsCerts(newCongratsCerts)
        setShowCongrats(newCongratsCerts.length > 0)
      }
    } catch (err) {
      console.error('Error fetching certificates:', err)
      setError('Failed to load certificates')
    } finally {
      setLoading(false)
    }
  }

  const fetchCertificates = fetchAll

  const handleViewCertificate = async (certificateId, canDownload) => {
    if (!canDownload) {
      toast.error('Pay your full course tuition balance to access this certificate.')
      return
    }
    try {
      const response = await api.get(`/certificates/view/${certificateId}`, {
        responseType: 'blob'
      })

      // Create a blob URL and open in new tab
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      window.open(url, '_blank')

      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 100)
    } catch (err) {
      console.error('Error viewing certificate:', err)
      alert('Failed to view certificate')
    }
  }

  const handleDownload = async (certificateId, certificateNumber, canDownload) => {
    if (!canDownload) {
      toast.error('Pay your full course tuition balance to download this certificate.')
      return
    }
    try {
      const response = await api.get(`/certificates/download/${certificateId}`, {
        responseType: 'blob'
      })

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `certificate-${certificateNumber}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error downloading certificate:', err)
      alert('Failed to download certificate')
    }
  }

  const handleViewMasterclassCert = async (certNumber) => {
    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api'
      const token = localStorage.getItem('token')
      const res = await fetch(`${apiBase}/masterclass/my-certificates/${certNumber}/view`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to fetch certificate')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 5000)
    } catch (err) {
      console.error(err)
      toast.error('Failed to view certificate')
    }
  }

  const handleDownloadMasterclassCert = async (certNumber) => {
    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api'
      const token = localStorage.getItem('token')
      const res = await fetch(`${apiBase}/masterclass/my-certificates/${certNumber}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to download certificate')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `SMA-Certificate-${certNumber}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error(err)
      toast.error('Failed to download certificate')
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const dismissCongrats = () => {
    setShowCongrats(false)
    // Mark all current certificate IDs as seen so congrats doesn't reappear on next visit
    if (certificates.length > 0) {
      markCongratsSeen(certificates.map(c => c.id))
    }
  }

  if (loading) return <LoadingSpinner label="Loading certificates" />

  if (error) return (
    <div className="pl-4 border-l-2 border-red-500/40">
      <p className="font-sans text-sm text-red-400">{error}</p>
    </div>
  )

  return (
    <div className="space-y-10">
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-1">Student Portal</span>
          <h1 className="font-display text-2xl text-white font-light">My Certificates</h1>
          <p className="font-sans text-xs text-cinema-muted mt-1">Available when your course tuition balance is cleared</p>
        </div>
      </div>

      {/* Phase 4E: Congratulations panel for newly available certificates */}
      {showCongrats && congratsCerts.length > 0 && (
        <CongratulationsPanel
          certificates={congratsCerts}
          onDismiss={dismissCongrats}
          onView={handleViewCertificate}
          onDownload={handleDownload}
        />
      )}

      {certificates.length === 0 ? (
        <div className="space-y-8">
          {/* Phase 4E: Certificate preview for students with no certificates yet */}
          <div className="cinema-empty">
            <Award size={36} className="mx-auto mb-4 text-cinema-muted/20" />
            <h3 className="font-display text-base text-white font-light mb-2">No Certificates Yet</h3>
            <p className="font-sans text-xs text-cinema-muted mb-6">Complete a course to earn your first certificate.</p>
          </div>

          {/* Phase 4E: "What you'll receive" preview */}
          <div className="pt-6 border-t border-white/5">
            <div className="cinema-section-header mb-6">
              <h2 className="cinema-section-title">Your Certificate</h2>
              <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-0.5">
                Complete all lessons and meet the required completion conditions to unlock your certificate.
              </p>
            </div>
            <CertificatePreview />
          </div>
        </div>
      ) : (
        <div className="pt-8 border-t border-white/5">
          <div className="cinema-section-header">
            <h2 className="cinema-section-title">Course Completion Certificates</h2>
          </div>
          <div className="cinema-list">
            {certificates.map((cert) => (
              <div key={cert.id} className="cinema-list-row flex-col !items-stretch py-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`shrink-0 ${cert.can_download ? 'text-cinema-glow' : 'text-amber-400'}`}>
                      <Award size={18} />
                    </div>
                    <div>
                      <p className="font-sans text-sm text-white font-medium">Course Completion</p>
                      <p className="font-mono text-xs text-cinema-muted mt-0.5">{cert.certificate_number}</p>
                    </div>
                  </div>
                  {cert.can_download && <CheckCircle size={16} className="text-green-400 shrink-0" />}
                </div>

                <div className="space-y-3">
                  <div>
                    <h4 className="font-display text-base sm:text-lg text-white font-light">{cert.course_title}</h4>
                    <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-0.5">{cert.course_code}</p>
                  </div>

                  <div className="flex flex-wrap gap-4">
                    {cert.final_grade && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-sans font-medium bg-green-500/10 text-green-400 border border-green-500/25 rounded-sm">
                        Grade: {cert.final_grade}
                      </span>
                    )}
                    {cert.duration_weeks && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-sans font-medium bg-white/5 text-cinema-muted border border-white/10 rounded-sm">
                        {cert.duration_weeks} weeks
                      </span>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 font-sans text-xs sm:text-sm text-cinema-muted">
                      <Calendar size={13} /> Completed: {formatDate(cert.completion_date)}
                    </div>
                    <div className="flex items-center gap-2 font-sans text-xs sm:text-sm text-cinema-muted">
                      <Calendar size={13} /> Issued: {formatDate(cert.issued_date)}
                    </div>
                  </div>

                  {cert.skills_acquired && (
                    <p className="font-sans text-xs sm:text-sm text-cinema-muted/80 italic border-t border-white/5 pt-3 leading-relaxed">{cert.skills_acquired}</p>
                  )}
                </div>

                {/* Locked notice */}
                {!cert.can_download && (
                  <div className="mt-4 pl-3.5 border-l-2 border-amber-500/40 flex items-start gap-2.5">
                    <Lock size={14} className="text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-sans text-xs sm:text-sm text-amber-300 font-medium mb-0.5">Certificate locked</p>
                      <p className="font-sans text-xs text-cinema-muted">{cert.certificate_locked_reason || 'Clear your tuition balance to download.'}</p>
                      {cert.balance_due > 0 && (
                        <p className="font-sans text-xs text-cinema-muted mt-1 font-mono">Balance due: {Number(cert.balance_due).toLocaleString()} UGX</p>
                      )}
                      <Link to="/student/payments" className="cinema-btn-text text-xs sm:text-sm mt-1.5 inline-block font-medium">Go to Payments →</Link>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => handleDownload(cert.id, cert.certificate_number, cert.can_download)}
                    disabled={!cert.can_download}
                    className="flex-1 cinema-btn-primary text-xs sm:text-sm py-2.5 min-h-[44px] flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                  >
                    <Download size={14} /> Download PDF
                  </button>
                  <button
                    onClick={() => handleViewCertificate(cert.id, cert.can_download)}
                    disabled={!cert.can_download}
                    aria-label="View certificate"
                    className="cinema-btn-ghost text-xs sm:text-sm py-2.5 px-3 min-h-[44px] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                    title="View"
                  >
                    <ExternalLink size={14} />
                  </button>
                </div>

                {/* Phase 4E: Share Achievement section */}
                <ShareAchievement certificate={cert} />
              </div>
            ))}
          </div>
        </div>
      )}

      {certificates.length > 0 && (
        <div className="pl-4 border-l-2 border-cinema-glow/30 flex items-start gap-3">
          <Award size={14} className="text-cinema-glow shrink-0 mt-0.5" />
          <p className="font-sans text-xs text-cinema-muted">
            These are official proof of course completion. Download as PDF and share on LinkedIn or with employers.
          </p>
        </div>
      )}

      {/* ── Masterclass Certificates of Attendance ───────────────────── */}
      <div className="pt-8 border-t border-white/5">
        <div className="cinema-section-header flex items-center justify-between">
          <div>
            <h2 className="cinema-section-title">Masterclass Certificates</h2>
            <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-0.5">
              Awarded for attending each SMA Friday Masterclass session
            </p>
          </div>
        </div>

        {masterclassCerts.length === 0 ? (
          <div className="cinema-empty">
            <Star size={32} className="mx-auto mb-3 text-cinema-muted/20" />
            <h3 className="font-display text-base text-white font-light mb-1">No Masterclass Certificates Yet</h3>
            <p className="font-sans text-xs sm:text-sm text-cinema-muted max-w-sm mx-auto">
              Attend a Friday Masterclass and check in to earn your Certificate of Attendance.
            </p>
          </div>
        ) : (
          <div className="cinema-list">
            {masterclassCerts.map((cert) => (
              <div key={cert.id} className="cinema-list-row flex-col !items-stretch py-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="font-sans text-xs text-cinema-glow font-semibold uppercase tracking-wider mb-1">Certificate of Attendance</p>
                    <p className="font-display text-lg text-white font-light">{cert.topic || 'SMA Friday Masterclass'}</p>
                    <p className="font-mono text-xs text-cinema-muted mt-0.5">{cert.certificate_number}</p>
                  </div>
                  <Star size={18} className="text-cinema-glow shrink-0" />
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-1.5 font-sans text-xs sm:text-sm text-cinema-muted">
                    <Calendar size={13} />
                    {cert.event_date ? new Date(cert.event_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </div>
                  <div className="flex items-center gap-1.5 font-sans text-xs sm:text-sm text-cinema-muted">
                    <CheckCircle size={13} className="text-green-400" />
                    Attended
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleDownloadMasterclassCert(cert.certificate_number)}
                    className="flex-1 cinema-btn-primary text-xs sm:text-sm py-2 min-h-[44px] flex items-center justify-center gap-1.5 font-medium"
                  >
                    <Download size={14} /> Download PDF
                  </button>
                  <button
                    onClick={() => handleViewMasterclassCert(cert.certificate_number)}
                    aria-label="View masterclass certificate"
                    className="cinema-btn-ghost text-xs sm:text-sm py-2 px-3 min-h-[44px] flex items-center gap-1.5 font-medium"
                    title="View"
                  >
                    <ExternalLink size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
